import { Stepper } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { ArrowLeft } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AssetFormData } from "../../types";
import Step1BasicInfo from "./BasicInfo";
import Step3ConditionAssessment from "./ConditionAssessment";
import Step4Documentation from "./Documentation";
import Step2SpecificInfo from "./SpecificInfo";
import { PageHeader } from "@shared/components/PageHeader";

interface AddAssetLandingProps {
  editMode?: boolean;
  initialData?: Partial<AssetFormData>;
  assetId?: string;
}

const AddAssetLanding: React.FC<AddAssetLandingProps> = ({
  editMode = false,
  initialData = {},
  assetId,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id, action } = useParams();
  const handleComplete = async (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => {
    try {
      // Aggregate all step data into final form data
      const finalData = Object.values(allStepData).reduce((acc, stepData) => {
        return { ...acc, ...stepData };
      }, {} as AssetFormData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast({
        title: editMode ? "Asset Updated" : "Asset Created",
        description: editMode
          ? `Asset "${finalData.assetName}" updated successfully`
          : `Asset "${finalData.assetName}" created successfully (ID: ${finalData.assetId})`,
      });

      // Redirect after success
      setTimeout(() => {
        window.location.href = "/ax/assetRegistry/assets";
      }, 1500);
    } catch (error) {
      toast({
        title: "Error",
        description: editMode
          ? "Failed to update asset"
          : "Failed to create asset",
        variant: "destructive",
      });
    }
  };

  const stepperSteps = [
    {
      id: "basic-info",
      title: "Basic Info",
      description: "Asset identification",
      component: <Step1BasicInfo editMode={editMode} assetId={assetId} />,
      slug: "basic-info",
    },
    {
      id: "specific-info",
      title: "Specific Info",
      description: "Asset-specific data",
      component: <Step2SpecificInfo editMode={editMode} />,
      slug: "specific-info",
    },
    {
      id: "condition",
      title: "Condition",
      description: "Condition assessment",
      component: <Step3ConditionAssessment editMode={editMode} />,
      slug: "condition",
    },
    {
      id: "documentation",
      title: "Documentation",
      description: "Files & documents",
      component: <Step4Documentation editMode={editMode} />,
      slug: "documentation",
    },
  ];

  return (
    <div className="space-y-4">
   <div className="mb-4 flex items-center gap-4">
  {/* Back Arrow */}
  <ArrowLeft
    className="w-5 h-5 cursor-pointer text-gray-700"
    onClick={() => navigate("/ax/asset-registry/assets")}
  />

  {/* Page Header */}
  <div className="flex flex-col">
    <PageHeader
      title={action === "edit" ? "Edit Asset" : "Add New Asset"}
      subtitle={
        action === "edit"
          ? `Update asset record: ${assetId || ""}`
          : "Create a new asset record in your system"
      }
    />
  </div>
</div>


      <Stepper
        steps={stepperSteps}
        onComplete={handleComplete}
        autoSave={true}
        persistProgress={true}
        storageKey={editMode ? `edit-asset-${assetId}` : "add-asset-progress"}
        allowSkip={false}
        enableRouting={true}
        className="w-full"
        allowStepClick={false}
        enableDraftMode={false}
        showDraftResume={false}
        onStepChange={undefined}
        onValidation={undefined}
        onStepSave={undefined}
      />
    </div>
  );
};

export default AddAssetLanding;
